import type { SaveState } from "../pages/builder";
import { Card } from "./components";
import { formatDate } from "./format";
import { VersionRow } from "./VersionRow";

/**
 * Versionen-Karte (E8) — gesendete Fassung + aktueller Autosave-Entwurf.
 * Reiner Ortswechsel aus `builder.tsx` (Schritt 4, Einheit A): keine neue Logik, kein State.
 *
 * B1 (UX-Polish 25.08.2026): Die Karte ist im Summen-Panel nach UNTEN gewandert und laesst sich
 * ueber `kompakt` verkleinern. Grund: sie beantwortet eine Rueckfrage („ist mein Stand
 * gespeichert, was ging schon raus?"), keine Alltagsfrage — zwischen der Preissumme und dem
 * Knopf, der sie verschickt, hat sie nichts verloren. Der Speicher-Status selbst steht seit A6
 * ohnehin fest in der Kopfzeile (`ui/SpeicherStatus.tsx`); was hier bleibt, ist die Historie.
 *
 * UX-Paket 26.08.2026, Punkt 5: `kompakt` verzichtet jetzt zusätzlich auf die volle `.card`-Optik
 * (Rand+Schatten+Hintergrund) — nur noch eine dünne Trennlinie nach oben. Der Rückfrage-Block war
 * trotz kleinerer Schrift optisch genauso "schwer" wie der eigentliche Haupt-CTA ("Senden
 * vorbereiten …", der außerhalb der Sende-Reife nur `btn-secondary` ist) und konkurrierte damit
 * um Aufmerksamkeit, die er als Rückfrage-Antwort nicht beanspruchen sollte.
 */
export function VersionenKarte({
  offerSentAt,
  currentOfferVersion,
  saveState,
  kompakt = false,
}: {
  offerSentAt: string | null | undefined;
  currentOfferVersion: number | undefined;
  saveState: SaveState;
  /** B1: schmalere Karte fuer den Fuss der rechten Spalte. Ohne den Schalter bleibt die
   *  bisherige Groesse — die Komponente wird auch anderswo gerendert werden koennen, und ein
   *  stiller Groessenwechsel an allen Einsatzorten waere eine Nebenwirkung, keine Absicht. */
  kompakt?: boolean;
}) {
  // `Card` rendert eine feste `card`-Klasse — der `kompakt`-Zweig braucht stattdessen die
  // schmale Trennlinien-Optik ohne `.card` (s. Kopfkommentar UX-Paket 26.08.2026 Punkt 5), darum
  // hier keine Klassen-Ternary auf einem gemeinsamen Root, sondern zwei Wurzel-Varianten mit
  // geteiltem Inhalt — visuell/funktional identisch zum vorherigen `<div className={...}>`.
  const inhalt = (
    <>
      <div className={`flex items-center gap-2 ${kompakt ? "mb-1.5" : "mb-2.5"}`}>
        <span
          className={
            kompakt
              ? "text-[10.5px] font-medium text-on-surface-variant/70 dark:text-secondary-fixed-dim/70"
              : "text-[12.5px] font-semibold text-on-background dark:text-inverse-on-surface"
          }
        >
          Versionen
        </span>
        <span className={`ml-auto text-on-surface-variant/60 ${kompakt ? "text-[9.5px]" : "text-[11px]"}`}>automatisch gespeichert</span>
      </div>
      <div className={kompakt ? "space-y-1" : "space-y-2"}>
        {offerSentAt && <VersionRow kompakt={kompakt} badge={`v${currentOfferVersion}`} title="Gesendet" sub={formatDate(offerSentAt)} />}
        <VersionRow
          active
          kompakt={kompakt}
          badge={`v${(currentOfferVersion ?? 0) + (offerSentAt ? 1 : 0) || 1}`}
          title="Aktueller Entwurf"
          sub={
            saveState === "saving"
              ? "Speichert …"
              : saveState === "conflict"
                ? "Konflikt – neu laden"
                : saveState === "error"
                  ? "Nicht gespeichert"
                  : "Gespeichert"
          }
        />
      </div>
    </>
  );
  return kompakt ? (
    <div className="border-t border-outline-variant/30 pt-3 dark:border-outline/20">{inhalt}</div>
  ) : (
    <Card className="p-4">{inhalt}</Card>
  );
}
